import React, {
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  Menu,
  MenuItem,
  type MenuOption,
  Tooltip,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_CRT_MED,
  CF_WRN_STL,
  IS_READ_ONLY,
  LIST_ACTIONS,
  TABS_FILTERS,
  VIOLATIONS_DETAILS_TABS,
  VIOLATIONS_TAB_LIST_ACTIONS,
  VIOLATIONS_TAB_LIST_FOOTER_KIND,
  violationsHome,
} from '../../../../shared/constants';
import { useInvertedLocalize, useNavigateTo } from '../../../../shared/hooks';
import { globalActions } from '../../../../shared/store/globalSlice';
import { type ViolationsTabSidePanelRowContextState } from '../../data-layer/violationsTabTypes';
import { violationsTabActions } from '../../data-store/violationsTabSlice';

import './ViolationsListActions.css';

/**
 * Props interface for ViolationsListActionsWrapper component
 * @interface
 */
interface ViolationsListActionsWrapperProps {
  children: ReactNode;
  kind: string | undefined;
  className: string;
}

/**
 * A wrapper component that renders violation list actions either in a footer or standard layout
 *
 * @param {ViolationsListActionsWrapperProps} props - Component props
 * @param {ReactNode} props.children - Child elements to be rendered
 * @param {VIOLATIONS_TAB_LIST_FOOTER_KIND} props.kind - Determines if the actions should be rendered in footer style
 * @returns {JSX.Element} Rendered component
 */
function ViolationsListActionsWrapper({
  children,
  kind,
  className,
}: ViolationsListActionsWrapperProps): JSX.Element {
  const footerActionClasses = cx(
    'Sod-ViolationsListActions--isFooter',
    className
  );
  const listActionClasses = cx('Sod-ViolationsListActions', className);

  if (kind === VIOLATIONS_TAB_LIST_FOOTER_KIND.FOOTER) {
    return (
      <FooterActionBar className={footerActionClasses}>
        <div />
        <Box className='Sod-ViolationsListActions-footerWrapper'>
          {children}
        </Box>
      </FooterActionBar>
    );
  }

  return <Box className={listActionClasses}>{children}</Box>;
}

interface ViolationsListActionsProps {
  kind?: string;
  tabInAction: string | null;
  isDisabled: boolean;
  rowContext: ViolationsTabSidePanelRowContextState;
  className?: string | null;
}

/**
 * ViolationsListActions
 */
function ViolationsListActions({
  kind,
  tabInAction,
  isDisabled,
  rowContext,
  className,
}: ViolationsListActionsProps): ReactElement | null {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const { navigateToDetails } = useNavigateTo();

  const [optionsWithIcons, setOptionsWithIcons] = useState([] as MenuOption[]);

  const isFooterKind = Boolean(kind === VIOLATIONS_TAB_LIST_FOOTER_KIND.FOOTER);
  const isMitigatedTab = Boolean(
    tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value
  );

  // Populate the list actions
  useEffect(() => {
    if (tabInAction === null) {
      return;
    }

    // Case: Mitigated tab, not in review, count > 1
    if (
      tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value &&
      Boolean(rowContext.mitigatingControlCount) &&
      Number(rowContext.mitigatingControlCount) > 1
    ) {
      // IS_READ_ONLY only VIEWDETAILS action left
      const updatedOptions = VIOLATIONS_TAB_LIST_ACTIONS[tabInAction].map(
        (option) => {
          const isCountableLabel = Boolean(
            option.value === LIST_ACTIONS.REMOVEMC.value ||
              option.value === LIST_ACTIONS.CHNAGEMCENDDATE.value
          );
          const isViewDetailsAction = Boolean(
            option.value === LIST_ACTIONS.VIEWDETAILS.value
          );

          if (isCountableLabel) {
            return {
              ...option,
              label: `${option.label} (${
                rowContext.mitigatingControlCount ?? 0
              })`,
              isDisabled: !isViewDetailsAction && isDisabled,
            };
          }

          return { ...option, isDisabled: !isViewDetailsAction && isDisabled };
        }
      );

      if (isFooterKind) {
        setOptionsWithIcons(
          updatedOptions.filter(
            (option) => option.value !== LIST_ACTIONS.REMEDIATE.value
          )
        );

        return;
      }

      setOptionsWithIcons(updatedOptions);

      return;
    }

    // Default case
    // IS_READ_ONLY only VIEWDETAILS action left
    const defaultOptions = VIOLATIONS_TAB_LIST_ACTIONS[tabInAction].map(
      (option) => {
        const isViewDetailsAction = Boolean(
          option.value === LIST_ACTIONS.VIEWDETAILS.value
        );

        return {
          ...option,
          isDisabled: !isViewDetailsAction && isDisabled,
        };
      }
    );

    setOptionsWithIcons(defaultOptions);
  }, [
    tabInAction,
    rowContext.mitigatingControlCount,
    isFooterKind,
    isDisabled,
  ]);

  const handleOptionClick = (option: {
    value: string;
    label: string;
    icon: string;
  }) => {
    if (option.value === LIST_ACTIONS.REMEDIATE.value) {
      dispatch(violationsTabActions.setIsRemediateModalOpen(true));
    }

    if (option.value === LIST_ACTIONS.MOVETOINREVIEW.value) {
      dispatch(violationsTabActions.setIsReviewModalOpen(true));
    }

    if (option.value === LIST_ACTIONS.VIEWDETAILS.value) {
      navigateToDetails({
        key: rowContext.id,
        params: {
          title: `${rowContext.title ?? ''}`,
          user: `${rowContext.username ?? ''}`,
          type: violationsHome,
          activeTab:
            tabInAction === TABS_FILTERS.CLOSEDCOUNT.value
              ? VIOLATIONS_DETAILS_TABS.TAB_SUMMARY
              : VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS,
        },
      });
    }
  };

  const wrapperClasses = cx(
    optionsWithIcons.length === 0 && 'Sod-ViolationsListActions--isEmpty',
    isMitigatedTab && 'Sod-ViolationsListActions--isMitigatedTab',
    className
  );

  const handleMitigateOpen = useCallback(() => {
    batch(() => {
      dispatch(violationsTabActions.setIsMitigateModalOpen(true));
      dispatch(
        violationsTabActions.setMitigateModalData({
          rowId: rowContext.id,
        })
      );
    });
  }, [dispatch, rowContext.id]);

  const getExpireIcon = useCallback(() => {
    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiredCount &&
      rowContext.isExpiredCount > 0
    ) {
      return <Icon kind='AlertCriticalFilled' color={CF_CRT_MED} />;
    }

    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiringSoonCount &&
      rowContext.isExpiringSoonCount > 0
    ) {
      return <Icon kind='AlertWarningFilled' color={CF_WRN_STL} />;
    }

    return null;
  }, [
    rowContext.isExpiredCount,
    rowContext.isExpiringSoonCount,
    rowContext.mitigatingControlCount,
  ]);

  const getToolTipMessage = useCallback(() => {
    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiredCount &&
      rowContext.isExpiredCount > 0
    ) {
      return `${rowContext.isExpiredCount} of the ${rowContext.mitigatingControlCount} mitigating controls have expired`;
    }

    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiringSoonCount &&
      rowContext.isExpiringSoonCount > 0
    ) {
      return `${rowContext.isExpiringSoonCount} of the ${rowContext.isExpiringSoonCount} mitigating controls have expired`;
    }

    return undefined;
  }, [
    rowContext.isExpiredCount,
    rowContext.isExpiringSoonCount,
    rowContext.mitigatingControlCount,
  ]);

  const renderListPrimaryButton = useCallback(() => {
    if (tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value) {
      return (
        <Box
          tag='div'
          className='Sod-ViolationsListActions-primaryBtnWrapper--isMitigatedTab'>
          <Tooltip
            trigger={
              <>
                <Chip
                  kind='subtleFilled'
                  label={
                    rowContext.mitigatingControlCount &&
                    rowContext.mitigatingControlCount > 1
                      ? `Mitigated (${rowContext.mitigatingControlCount})`
                      : 'Mitigated'
                  }
                  color='info'
                  size='small'
                  isInteractionDisabled
                />
                {getExpireIcon()}
              </>
            }
            text={getToolTipMessage()}
            placement='bottom'
          />
        </Box>
      );
    }

    if (tabInAction === TABS_FILTERS.OPENCOUNT.value) {
      return (
        <Tooltip
          trigger={
            <Button
              type='button'
              kind='subtle'
              size='medium'
              isDisabled={isDisabled}
              onClick={handleMitigateOpen}>
              {l('Mitigate')}
            </Button>
          }
          isPortalEnabled
          text='The new experience is does not support any actions yet. Please return to the classic view to take actions.'
          placement='bottom'
        />
      );
    }

    return null;
  }, [
    l,
    tabInAction,
    isDisabled,
    rowContext.mitigatingControlCount,
    getExpireIcon,
    getToolTipMessage,
    handleMitigateOpen,
  ]);

  const renderFooterPrimaryButton = useCallback(
    () => (
      <Tooltip
        trigger={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            isDisabled={isDisabled}
            onClick={handleMitigateOpen}>
            {l(isMitigatedTab ? 'Remediate' : 'Mitigate')}
          </Button>
        }
        text={l(
          'The new experience is does not support any actions yet. Please return to the classic view to take actions.'
        )}
        placement='top'
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, isDisabled, isMitigatedTab]
  );

  if (tabInAction === null) {
    return null;
  }

  const getReadOnlyMenu = () => (
    <Box tag='div' className='Sod-ViolationsListActions--isReadOnlyWrapper'>
      <MenuItem
        option={{
          label: l('View Details'),
          value: 'view_details',
        }}
        onClick={() => {
          navigateToDetails({
            key: rowContext.id,
            params: {
              title: `${rowContext.title ?? ''}`,
              user: `${rowContext.username ?? ''}`,
              type: violationsHome,
              activeTab:
                tabInAction === TABS_FILTERS.CLOSEDCOUNT.value
                  ? VIOLATIONS_DETAILS_TABS.TAB_SUMMARY
                  : VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS,
              username: `${rowContext.username ?? ''}`,
            },
          });
        }}
      />
      <span className='Sod-ViolationsListActions--isDivider' />
      <MenuItem
        option={{
          label: l('Return to Classic Experience to Take Actions'),
          secondaryLabel: l("The new experience doesn't support actions yet."),
          value: 'return_to_classic',
        }}
        onClick={() => {
          dispatch(globalActions.setIsClassisExperience(true));
        }}
        className='Sod-ViolationsListActions--isReturnToClassicBtn'
      />
    </Box>
  );

  const getReadOnlyBtns = () => {
    if (
      tabInAction === TABS_FILTERS.OPENCOUNT.value ||
      tabInAction === TABS_FILTERS.INREVIEWCOUNT.value
    ) {
      return (
        <Button
          type='button'
          kind='outlined'
          size='medium'
          isDisabled={isDisabled}
          onClick={() => {}}>
          {l('Mitigate')}
        </Button>
      );
    }

    if (tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value) {
      return (
        <Button
          type='button'
          kind='outlined'
          size='medium'
          isDisabled={isDisabled}
          onClick={() => {}}>
          {l('Remediate')}
        </Button>
      );
    }

    return (
      <Button
        type='button'
        kind='outlined'
        size='medium'
        isDisabled={isDisabled}
        onClick={() => {}}>
        Generate Report
      </Button>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (IS_READ_ONLY) {
    return (
      <ViolationsListActionsWrapper kind={kind} className={wrapperClasses}>
        {isFooterKind && (
          <Tooltip
            trigger={getReadOnlyBtns()}
            text='The new experience is does not support any actions yet. Please return to the classic view to take actions.'
            placement='top'
          />
        )}
        <Menu
          CustomMenu={getReadOnlyMenu()}
          isPortalEnabled
          trigger={
            <ButtonIcon
              size='medium'
              kind='ghost'
              icon={
                <Icon kind='MoreVertical' color='neutral-100' size='medium' />
              }
            />
          }
        />
      </ViolationsListActionsWrapper>
    );
  }

  return (
    <ViolationsListActionsWrapper kind={kind} className={wrapperClasses}>
      {isFooterKind ? renderFooterPrimaryButton() : renderListPrimaryButton()}
      {optionsWithIcons.length > 0 && (
        <Menu
          placement='bottom'
          options={optionsWithIcons}
          onChange={handleOptionClick}
          minOptionsForSearch={optionsWithIcons.length + 1}
          trigger={
            <ButtonIcon
              size='medium'
              kind='ghost'
              icon={<Icon kind='MoreVertical' />}
            />
          }
          isPortalEnabled
        />
      )}
    </ViolationsListActionsWrapper>
  );
}

ViolationsListActions.defaultProps = {
  kind: VIOLATIONS_TAB_LIST_FOOTER_KIND.LIST,
  className: null,
};

export default memo(ViolationsListActions);
